import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import dynamic from "next/dynamic";
import { useTranslation } from "next-i18next";

import { loadI18nNamespaces } from "../../../hooks/useLoadI18nNamespaces";
import { MidnightVerificationSession } from "./types";

const MIDNIGHT_VERIFICATION_LOCALIZATION_NAMESPACES = ["load-form"] as const;

const MidnightVerificationPopup = dynamic(() => import("./MidnightVerificationPopup"), {
    ssr: false
});

interface MidnightVerificationContextValue {
    isMidnightVerificationOpen: boolean;
    openMidnightVerification: (session: MidnightVerificationSession) => void;
    closeMidnightVerification: () => void;
}

const MidnightVerificationContext = createContext<MidnightVerificationContextValue | null>(null);

/**
 * Standalone midnight verification module.
 * Owns its own popup / payment / expire lifecycle — independent of compact load posting UI.
 */
export function MidnightVerificationProvider({ children }: React.PropsWithChildren<{}>) {
    const { i18n } = useTranslation();
    const [session, setSession] = useState<MidnightVerificationSession | null>(null);

    const openMidnightVerification = useCallback(
        (nextSession: MidnightVerificationSession) => {
            void loadI18nNamespaces(i18n, MIDNIGHT_VERIFICATION_LOCALIZATION_NAMESPACES).then(() => {
                setSession(nextSession);
            });
        },
        [i18n]
    );

    const closeMidnightVerification = useCallback(() => {
        setSession(null);
    }, []);

    const value = useMemo(
        () => ({
            isMidnightVerificationOpen: !!session,
            openMidnightVerification,
            closeMidnightVerification
        }),
        [closeMidnightVerification, openMidnightVerification, session]
    );

    return (
        <MidnightVerificationContext.Provider value={value}>
            {children}
            {session ? (
                <MidnightVerificationPopup
                    key={`midnight-${session.loadId}`}
                    isOpen={!!session}
                    session={session}
                    onClose={closeMidnightVerification}
                />
            ) : null}
        </MidnightVerificationContext.Provider>
    );
}

export function useMidnightVerification() {
    const context = useContext(MidnightVerificationContext);

    if (!context) {
        throw new Error("useMidnightVerification must be used within MidnightVerificationProvider");
    }

    return context;
}

/** Non-throwing variant for surfaces that may render outside the provider tree. */
export function useMidnightVerificationOptional() {
    return useContext(MidnightVerificationContext);
}
